import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { I18nService, StaticService } from '@app/i18n/services';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { FormsService } from '@services/forms';
import { ProgressIndicatorService } from '@services/progress';
import { IManagedUser, IManagedUserUpdate, OptInMask, RidesMask, UsersService } from '@services/users';
import { bitClear, bitSet, bitTest, deflateMask, FormValidators, inflateMask, updateMultipleBits } from '@tcp13equals2/z5-lib';
import isEqual from 'lodash/isEqual';
import * as moment from 'moment-timezone';
import { merge } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { ClustersService, IClusterLocales, ICountry, IGender } from '../../../../../../services/clusters';
import { DialogService } from '../../../../../../services/dialog';
import { TimezonesService } from '../../../../../../services/timezones/timezones.service';
import { FormEditorInputType } from '../../../../../../shared/components';
import { IUsersEditUserAttributesDialogData } from './users-edit-user-attributes.models';

@Component({
	selector: 'app-users-edit-user-attributes',
	templateUrl: './users-edit-user-attributes.dialog.html',
	styleUrls: ['./users-edit-user-attributes.dialog.scss']
})
export class UsersEditUserAttributesDialogComponent implements OnInit {
	private i18nConfirmCognitoGroupsUpdate = this.i18nService.get('', 'app.prompt.users.edit-user-attributes.confirm-message');

	private dialogRefClose: () => void;
	private poundsPerKg = 2.20462262185;
	public phoneNumbers: { [key: string]: string } = {};
	public addresses: {
		[key: string]: {
			type: string;
			street: string;
			city: string;
			state: string;
			zip: string;
			country: string;
		};
	} = {};
	public locales: { id: string; label: string }[] = [];
	public genders: { id: string; label: string }[] = [];
	public units: { id: string; label: string }[] = [];
	public countries: { id: string; label: string }[] = [];
	public expandedTimezoneArea = '';
	public formEditorInputType = FormEditorInputType;
	public errorStateMatcher = new ErrorStateMatcher();
	public formInputs = new FormGroup({
		firstname: new FormControl('', [this.formsService.required()]),
		lastname: new FormControl('', [this.formsService.required()]),
		height: new FormControl(0, [this.formsService.min(1, true)]),
		heightFt: new FormControl(0, [this.heightMinValidator()]),
		heightIn: new FormControl(0, [this.heightMinValidator()]),
		weight: new FormControl(0, [this.formsService.min(1, true)]),
		weightLb: new FormControl(0, [this.formsService.min(2.205, true)]),
		gender: new FormControl(''),
		dob: new FormControl(null),
		avatar: new FormControl('', [FormValidators.url], [this.formsService.urlValid()]),
		timezone: new FormControl('', [Validators.required]),
		locale: new FormControl('', [Validators.required]),
		metric: new FormControl('', [Validators.required]),
		country: new FormControl(''),
		state: new FormControl(''),
		locality: new FormControl(''),
		billingCountry: new FormControl(''),
		addresses: new FormGroup({}),
		tns: new FormGroup({}),
		// User preference mask
		ccpa: new FormControl(false),
		// User settings mask
		beta: new FormControl(false),
		// User opt in mask
		mff: new FormControl(false),
		roval: new FormControl(false),
		specialized: new FormControl(false),
		retul: new FormControl(false),
		app: new FormControl(false),
		// Rides mask
		mountain: new FormControl(false),
		road: new FormControl(false),
		fitness: new FormControl(false),
		adventureOrTour: new FormControl(false),
		dirtOrPark: new FormControl(false),
		cyclocross: new FormControl(false),
		casualOrCommute: new FormControl(false),
		downhill: new FormControl(false),
		triathlon: new FormControl(false),
		pedalAssist: new FormControl(false)
	});

	constructor(
		private i18nService: I18nService,
		private feedbackService: FeedbackService,
		private usersService: UsersService,
		private progressIndicatorService: ProgressIndicatorService,
		private formsService: FormsService,
		@Inject(MAT_DIALOG_DATA) public dialogData: IUsersEditUserAttributesDialogData,
		private dialogRef: MatDialogRef<UsersEditUserAttributesDialogComponent>,
		public timezonesService: TimezonesService,
		private staticService: StaticService,
		private cdref: ChangeDetectorRef,
		private clustersService: ClustersService,
		private dialogService: DialogService
	) {
		// Store original close function and replace with unsaved version, remember to restore on use
		this.dialogRefClose = this.dialogService.interceptClose(this.dialogRef, this, this.closeDialog);
	}

	public ngOnInit(): void {
		this.progressIndicatorService.spinner(true);

		this.phoneNumbers = JSON.parse(JSON.stringify(this.dialogData.user.tns));
		this.addresses = JSON.parse(JSON.stringify(this.dialogData.user.addresses));

		this.getLocales();
		this.getTimezones();
		this.getGenders();
		this.getCountries();

		this.staticService.get('unitSystems').forEach((unit: { id: string; label: string; i18n: string }) => {
			this.units.push({
				id: unit.id,
				label: this.i18nService.get(unit.label, unit.i18n)
			});
		});

		for (const [key, value] of Object.entries(this.phoneNumbers)) {
			(this.formInputs.get('tns') as FormGroup).addControl(`${key}Type`, new FormControl(key, [Validators.required]));
			(this.formInputs.get('tns') as FormGroup).addControl(`${key}Value`, new FormControl(value));
		}

		for (const [key, value] of Object.entries(this.addresses)) {
			const addresses = this.formInputs.get('addresses') as FormGroup;
			addresses.addControl(`${key}Type`, new FormControl(key, [Validators.required]));
			addresses.addControl(`${key}Street`, new FormControl(value.street, [Validators.maxLength(255)]));
			addresses.addControl(`${key}City`, new FormControl(value.city, [Validators.maxLength(255)]));
			addresses.addControl(`${key}State`, new FormControl(value.state, [Validators.maxLength(255)]));
			addresses.addControl(`${key}Zip`, new FormControl(value.zip, [Validators.maxLength(255)]));
			addresses.addControl(`${key}Country`, new FormControl(value.country, [Validators.required]));
		}

		this.formInputs.patchValue({
			firstname: this.dialogData.user.firstname,
			lastname: this.dialogData.user.lastname,
			height: this.dialogData.user.height,
			heightFt: this.convertHeight(this.dialogData.user.height).ft,
			heightIn: this.convertHeight(this.dialogData.user.height).in,
			weight: this.dialogData.user.weight ? this.dialogData.user.weight : null,
			weightLb: this.convertWeight(this.dialogData.user.weight, 'imperial')
				? this.convertWeight(this.dialogData.user.weight, 'imperial')
				: null,
			gender: this.dialogData.user.gender ? this.dialogData.user.gender : '',
			dob: this.dialogData.user.dob ? moment.utc(this.dialogData.user.dob) : null,
			timezone: this.dialogData.user.timezone,
			metric: this.dialogData.user.metric,
			locale: this.dialogData.user.locale,
			billingCountry: this.dialogData.user.billingCountry,
			country: this.dialogData.user.country,
			state: this.dialogData.user.state,
			locality: this.dialogData.user.locality,
			ccpa: bitTest(this.dialogData.user.prefsMask, 9),
			beta: bitTest(this.dialogData.user.attsMask, 12),
			...inflateMask(this.dialogData.user.optInMask, OptInMask),
			...inflateMask(this.dialogData.user.ridesMask, RidesMask)
		});

		if (this.dialogData.user.avatar) {
			this.formInputs.controls.avatar.setAsyncValidators(this.formsService.urlValid([this.dialogData.user.avatar]));
		}

		if (this.dialogData.user.dob) {
			this.formInputs.get('dob').setValidators([Validators.required]);
		}

		if (this.dialogData.user.gender) {
			this.formInputs.get('gender').setValidators([Validators.required]);
		}

		if (this.dialogData.user.country) {
			this.formInputs.get('country').setValidators([Validators.required]);
		}

		if (this.dialogData.user.state) {
			this.formInputs.get('state').setValidators([Validators.required]);
		}

		if (this.dialogData.user.locality) {
			this.formInputs.get('locality').setValidators([Validators.required]);
		}

		if (this.dialogData.user.billingCountry) {
			this.formInputs.get('billingCountry').setValidators([Validators.required]);
		}

		if (Number.isFinite(this.dialogData.user.weight)) {
			this.formInputs.get('weight').setValidators([this.formsService.min(1, true), Validators.required]);
			this.formInputs.get('weightLb').setValidators([this.formsService.min(2.205, true), Validators.required]);
		}

		if (Number.isFinite(this.dialogData.user.height)) {
			this.formInputs.get('height').setValidators([this.formsService.min(1, true), Validators.required]);
			this.formInputs.get('heightFt').setValidators([this.heightMinValidator(), Validators.required]);
			this.formInputs.get('heightIn').setValidators([this.heightMinValidator(), Validators.required]);
		}

		// A weird form behavior when using patchValue causes the async validator to get stuck and not complete, setTimeout works around this
		setTimeout(() => {
			this.formInputs.patchValue({
				avatar: this.dialogData.user.avatar
			});

			this.formInputs.markAllAsTouched();
		});

		merge(this.formInputs.get('heightFt').valueChanges, this.formInputs.get('heightIn').valueChanges).subscribe(() => {
			if (!Number.isFinite(this.formInputs.get('heightFt').value) && !Number.isFinite(this.formInputs.get('heightIn').value)) {
				this.formInputs.get('height').setValue(null, { emitEvent: false });
			} else {
				this.formInputs.get('height').setValue(
					this.convertHeight({
						ft: this.formInputs.get('heightFt').value,
						in: this.formInputs.get('heightIn').value
					}).cm,
					{ emitEvent: false }
				);
			}

			this.formInputs.get('heightIn').updateValueAndValidity({ emitEvent: false });
			this.formInputs.get('heightFt').updateValueAndValidity({ emitEvent: false });
		});

		this.formInputs.get('height').valueChanges.subscribe(() => {
			if (!Number.isFinite(this.formInputs.get('height').value)) {
				this.formInputs.get('heightFt').setValue(null, { emitEvent: false });
				this.formInputs.get('heightIn').setValue(null, { emitEvent: false });
			} else {
				const height = this.convertHeight(this.formInputs.get('height').value);

				this.formInputs.get('heightFt').setValue(height.ft, { emitEvent: false });
				this.formInputs.get('heightIn').setValue(height.in, { emitEvent: false });
			}

			this.formInputs.get('heightIn').updateValueAndValidity({ emitEvent: false });
			this.formInputs.get('heightFt').updateValueAndValidity({ emitEvent: false });
		});

		this.formInputs.get('weightLb').valueChanges.subscribe(() => {
			const weight = Number.isFinite(this.formInputs.get('weightLb').value)
				? this.convertWeight(this.formInputs.get('weightLb').value, 'metric')
				: null;

			this.formInputs.get('weight').setValue(weight, {
				emitEvent: false
			});
		});

		this.formInputs.get('weight').valueChanges.subscribe(() => {
			const weight = Number.isFinite(this.formInputs.get('weight').value)
				? this.convertWeight(this.formInputs.get('weight').value, 'imperial')
				: null;

			this.formInputs.get('weightLb').setValue(weight, {
				emitEvent: false
			});
		});

		this.formInputs.get('metric').valueChanges.subscribe(() => {
			this.cdref.detectChanges();
			this.formInputs.get('height').updateValueAndValidity({ emitEvent: false });
			this.formInputs.get('heightIn').updateValueAndValidity({ emitEvent: false });
			this.formInputs.get('heightFt').updateValueAndValidity({ emitEvent: false });
		});
	}

	public heightMinValidator(): ValidatorFn {
		return (control: AbstractControl): { [key: string]: any } | null => {
			if (!Number.isFinite(this.formInputs?.get('heightFt').value) && !Number.isFinite(this.formInputs?.get('heightIn').value)) {
				return null;
			}

			const height = this.convertHeight({
				ft: this.formInputs?.get('heightFt').value,
				in: this.formInputs?.get('heightIn').value
			}).cm;

			// Only validate imperial inputs if visible, otherwise clear error
			if (this.formInputs && this.formInputs.controls.metric.value === 'imperial') {
				return height < 1 ? { minError: { value: true } } : null;
			}

			return null;
		};
	}

	public deleteAddress(address: { key: string; value: any }): void {
		delete this.addresses[address.key];

		setTimeout(() => {
			const addresses = this.formInputs.get('addresses') as FormGroup;

			addresses.removeControl(`${address.key}Type`);
			addresses.removeControl(`${address.key}Street`);
			addresses.removeControl(`${address.key}City`);
			addresses.removeControl(`${address.key}State`);
			addresses.removeControl(`${address.key}Zip`);
			addresses.removeControl(`${address.key}Country`);
			addresses.markAsDirty();
			addresses.updateValueAndValidity();
		});
	}

	public addAddress(): void {
		let index = 0;

		while (this.addresses.hasOwnProperty(`-newAddress${index}`)) {
			index++;
		}

		const addresses = this.formInputs.get('addresses') as FormGroup;

		addresses.addControl(`-newAddress${index}Type`, new FormControl('', [Validators.required]));
		addresses.addControl(`-newAddress${index}Street`, new FormControl('', [Validators.maxLength(255)]));
		addresses.addControl(`-newAddress${index}City`, new FormControl('', [Validators.maxLength(255)]));
		addresses.addControl(`-newAddress${index}State`, new FormControl('', [Validators.maxLength(255)]));
		addresses.addControl(`-newAddress${index}Zip`, new FormControl('', [Validators.maxLength(255)]));
		addresses.addControl(`-newAddress${index}Country`, new FormControl('', [Validators.required]));
		this.formInputs.markAllAsTouched();
		addresses.updateValueAndValidity();

		this.addresses[`-newAddress${index}`] = {
			type: '',
			street: '',
			city: '',
			state: '',
			zip: '',
			country: ''
		};
	}

	public deletePhoneNumber(phoneNumber: { key: string; value: string }): void {
		delete this.phoneNumbers[phoneNumber.key];

		setTimeout(() => {
			(this.formInputs.get('tns') as FormGroup).removeControl(`${phoneNumber.key}Type`);
			(this.formInputs.get('tns') as FormGroup).removeControl(`${phoneNumber.key}Value`);
			(this.formInputs.get('tns') as FormGroup).markAsDirty();
			(this.formInputs.get('tns') as FormGroup).updateValueAndValidity();
		});
	}

	public addPhoneNumber(): void {
		let index = 0;

		while (this.phoneNumbers.hasOwnProperty(`-newPhoneNumber${index}`)) {
			index++;
		}

		(this.formInputs.get('tns') as FormGroup).addControl(`-newPhoneNumber${index}Type`, new FormControl('', [Validators.required]));
		(this.formInputs.get('tns') as FormGroup).addControl(`-newPhoneNumber${index}Value`, new FormControl('', [Validators.required]));
		this.formInputs.markAllAsTouched();
		(this.formInputs.get('tns') as FormGroup).updateValueAndValidity();
		this.phoneNumbers[`-newPhoneNumber${index}`] = '';
	}

	public onFormSubmit(): void {
		if (Object.keys(this.getFormValues()).length === 0) {
			this.dialogRefClose();
		} else {
			this.feedbackService.confirm({
				title: FeedbackTitle.warning,
				message: this.i18nConfirmCognitoGroupsUpdate,
				style: FeedbackStyle.error,
				callback: (confirmed: boolean) => {
					if (confirmed) this.updateUserAttributes();
				}
			});
		}
	}

	public toggleTimezoneGroup(event: Event, area: string): void {
		this.expandedTimezoneArea = this.expandedTimezoneArea === area ? '' : area;
		event.preventDefault();
	}

	public formatDate(date: string | number): string {
		return new Date(date).toLocaleString();
	}

	private convertWeight(value: number, convertTo: string): number {
		if (convertTo === 'imperial') {
			return Math.round(value * this.poundsPerKg * 1000) / 1000;
		} else {
			return parseFloat((value / this.poundsPerKg).toFixed(6));
		}
	}

	private convertHeight(value: number | { ft: number; in: number }): { ft?: number; in?: number; cm?: number } {
		if (typeof value === 'number') {
			const total = value / 2.54;
			const feet = Math.floor(total / 12);
			const inches = Math.floor(total - 12 * feet);

			return { ft: feet, in: inches };
		} else if (value) {
			const feet = Number.isFinite(value.ft) ? value.ft : 0;
			const inches = Number.isFinite(value.in) ? value.in : 0;
			return { cm: Math.round(feet * 30.48 + inches * 2.54) };
		} else {
			return {};
		}
	}

	private getTimezones(): void {
		this.timezonesService.getTimezones().subscribe();
	}

	private getLocales(): void {
		this.clustersService.getClusterLocales().subscribe((res: IClusterLocales[]) => {
			this.locales = res
				.map((locale) => {
					return {
						id: locale.id.toLowerCase(),
						label: locale.name
					};
				})
				.sort((a, b) => {
					return a.label.localeCompare(b.label);
				});
		});
	}

	private getGenders(): void {
		this.clustersService.getClusterGenders().subscribe(
			(res: IGender[]) => {
				this.genders = res;
				this.isLoaded();
			},
			() => {
				this.staticService.get('genders').forEach((gender: { id: string; label: string; i18n: string }) => {
					this.genders.push({
						id: gender.id,
						label: this.i18nService.get(gender.label, gender.i18n)
					});
				});
				this.isLoaded();
			}
		);
	}

	private getCountries(): void {
		this.clustersService.getClusterCountries().subscribe(
			(res: ICountry[]) => {
				this.countries = res;
				this.isLoaded();
			},
			() => {
				this.staticService.get('countries').forEach((country: { id: string; label: string; i18n: string }) => {
					this.countries.push({
						id: country.id,
						label: this.i18nService.get(country.label, country.i18n)
					});
				});
				this.isLoaded();
			}
		);
	}

	private isLoaded(): void {
		if (this.countries.length > 0 && this.genders.length > 0) {
			this.progressIndicatorService.spinner(false);
		}
	}

	private getFormValues(): IManagedUserUpdate {
		const user: IManagedUserUpdate = {};

		for (const [controlName, control] of Object.entries(this.formInputs.controls)) {
			if (!control.dirty) continue;

			switch (controlName) {
				case 'height':
				case 'heightFt':
				case 'heightIn':
					const height = this.formInputs.value.height ? Math.round(this.formInputs.value.height) : undefined;

					if (!isEqual(this.dialogData.user?.height, height)) user['height'] = height;

					break;
				case 'weight':
				case 'weightLb':
					const weight = this.formInputs.value.weight ? parseFloat(this.formInputs.value.weight.toFixed(6)) : undefined;

					if (!isEqual(this.dialogData.user?.weight, weight)) user['weight'] = weight;

					break;
				case 'dob':
					const dob = this.formInputs.value.dob ? parseInt(this.formInputs.value.dob.format('x'), 10) : 0;

					if (!isEqual(this.dialogData.user?.dob, dob)) user['dob'] = dob;

					break;
				case 'addresses':
					const addresses = {};

					for (const [key] of Object.entries(this.addresses)) {
						let index = 1;
						let type = addresses.hasOwnProperty(this.formInputs.value.addresses[`${key}Type`])
							? this.formInputs.value.addresses[`${key}Type`] + index
							: this.formInputs.value.addresses[`${key}Type`];

						while (addresses.hasOwnProperty(type)) {
							index++;
							type = addresses.hasOwnProperty(this.formInputs.value.addresses[`${key}Type`])
								? this.formInputs.value.addresses[`${key}Type`] + index
								: this.formInputs.value.addresses[`${key}Type`];
						}

						addresses[type] = {
							type: this.formInputs.value.addresses[`${key}Type`],
							street: this.formInputs.value.addresses[`${key}Street`],
							city: this.formInputs.value.addresses[`${key}City`],
							state: this.formInputs.value.addresses[`${key}State`],
							zip: this.formInputs.value.addresses[`${key}Zip`],
							country: this.formInputs.value.addresses[`${key}Country`]
						};
					}

					const userAddresses = {};

					if (this.dialogData.user?.addresses) {
						for (const [key, address] of Object.entries(this.dialogData.user.addresses)) {
							userAddresses[key] = {
								street: address.street,
								city: address.city,
								state: address.state,
								zip: address.zip,
								country: address.country,
								type: key
							};
						}
					}

					if (!isEqual(userAddresses, addresses)) user['addresses'] = addresses;

					break;
				case 'tns':
					const tns = {};

					for (const [key] of Object.entries(this.phoneNumbers)) {
						let index = 1;
						let type = tns.hasOwnProperty(this.formInputs.value.tns[`${key}Type`])
							? this.formInputs.value.tns[`${key}Type`] + index
							: this.formInputs.value.tns[`${key}Type`];

						while (tns.hasOwnProperty(type)) {
							index++;
							type = tns.hasOwnProperty(this.formInputs.value.tns[`${key}Type`])
								? this.formInputs.value.tns[`${key}Type`] + index
								: this.formInputs.value.tns[`${key}Type`];
						}

						tns[type] = this.formInputs.value.tns[`${key}Value`];
					}

					if (!isEqual(this.dialogData.user?.tns, tns)) user['tns'] = tns;

					break;
				case 'ccpa':
					const prefsMask = this.formInputs.value.ccpa
						? bitSet(this.dialogData.user.prefsMask, 9)
						: bitClear(this.dialogData.user.prefsMask, 9);

					if (!isEqual(this.dialogData.user?.prefsMask, prefsMask)) user['prefsMask'] = prefsMask;

					break;
				case 'beta':
					const attsMask = updateMultipleBits([{ bit: 12, value: this.formInputs.value.beta }], this.dialogData.user.attsMask);

					if (!isEqual(this.dialogData.user?.attsMask, attsMask)) user['attsMask'] = attsMask;

					break;
				case 'mff':
				case 'roval':
				case 'specialized':
				case 'retul':
				case 'app':
					const { mff, roval, specialized, retul, app } = this.formInputs.value;
					const optInMask = deflateMask(
						{
							mff,
							roval,
							specialized,
							retul,
							app
						},
						OptInMask
					);

					if (!isEqual(this.dialogData.user?.optInMask, optInMask)) user['optInMask'] = optInMask;

					break;
				case 'mountain':
				case 'road':
				case 'fitness':
				case 'adventureOrTour':
				case 'dirtOrPark':
				case 'cyclocross':
				case 'casualOrCommute':
				case 'downhill':
				case 'triathlon':
				case 'pedalAssist':
					const {
						mountain,
						road,
						fitness,
						adventureOrTour,
						dirtOrPark,
						cyclocross,
						casualOrCommute,
						downhill,
						triathlon,
						pedalAssist
					} = this.formInputs.value;
					const ridesMask = deflateMask(
						{
							mountain,
							road,
							fitness,
							adventureOrTour,
							dirtOrPark,
							cyclocross,
							casualOrCommute,
							downhill,
							triathlon,
							pedalAssist
						},
						RidesMask
					);

					if (!isEqual(this.dialogData.user?.ridesMask, ridesMask)) user['ridesMask'] = ridesMask;

					break;
				default:
					let value = this.formInputs.value[controlName];

					if (typeof value === 'string') {
						value = value.trim();

						// For strings we have two scenarios:
						// - If value exists on original object we allow empty string values since they are used to clear value
						// - If value does not yet exist we block empty strings, otherwise empty spaces in form fields are detected as changes
						//   but they won't be saved so false positives.
						if (
							(this.dialogData.user.hasOwnProperty(controlName) && !isEqual(this.dialogData.user?.[controlName], value)) ||
							(value && !isEqual(this.dialogData.user?.[controlName], value))
						) {
							user[controlName] = value;
						}
					} else if (!isEqual(this.dialogData.user?.[controlName], value)) {
						user[controlName] = value;
					}
			}
		}

		return user;
	}

	private closeDialog(): void {
		if (Object.keys(this.getFormValues()).length > 0) {
			this.feedbackService.confirm({
				title: FeedbackTitle.warning,
				message: this.i18nService.get(
					'You have unsaved changes that will be lost. Are you sure you want to continue?',
					'app.prompt.pending-changes-confirm-message'
				),
				style: FeedbackStyle.error,
				callback: (confirmed: boolean) => {
					if (confirmed) this.dialogRefClose();
				}
			});
		} else {
			this.dialogRefClose();
		}
	}

	private updateUserAttributes(): void {
		this.progressIndicatorService.spinner(true);

		const user = this.getFormValues();
		const refreshSearch = Object.keys(user).length > 0;

		this.usersService
			.updateUserAttributes(
				this.dialogData.user.id,
				this.dialogData.user.identities['COGNITO'],
				user
			)
			.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
			.subscribe(
				(res: IManagedUser) => {
					this.dialogData.user = res;
					this.dialogData.callback(this.dialogData.user, refreshSearch);
					this.feedbackService.notification({
						msg: this.i18nService.get('User attributes updated.', 'app.descriptor.users.updated-user-attributes'),
						config: { duration: 4000 }
					});
					this.dialogRefClose();
				},
				(error: any) => this.feedbackService.apiError(error)
			);
	}
}
